import { roundMoney } from "../../../utils/formatPrice";

export const PROMO_TYPES = {
  FLAT: "FLAT",
  PERCENT: "PERCENT",
  QUANTITY_PRICE: "QUANTITY_PRICE",
  PACKAGE_PRICE: "PACKAGE_PRICE",
};
export const getPromoId = (promo) => promo?.promoId || promo?.Id || null;
export const isPackagePromo = (promo) =>
  promo?.promoType === PROMO_TYPES.PACKAGE_PRICE;
/** Types that price a whole quantity instead of a single unit. */
export const isQuantityBasedPromoType = (promoType) =>
  promoType === PROMO_TYPES.QUANTITY_PRICE ||
  promoType === PROMO_TYPES.PACKAGE_PRICE;
/** Units in one package, as frozen on the promotion when it was created. */
export const getPackageQuantity = (promo) =>
  isPackagePromo(promo) ? Number(promo.minQuantity) || 0 : 0;
/** A product may carry several promotions - never assume the package one is first. */
export const findPackagePromo = (product) =>
  product?.pricing?.promotions?.find(isPackagePromo) ?? null;
/** The only pack-quantity source in the app. */
export const getManufacturerPackQuantity = (product) =>
  Number(product?.logisticsDetails?.manufacturerPackQuantity) || 0;
export const hasValidPackQuantity = (product) =>
  getManufacturerPackQuantity(product) > 1;
export const getPromoValueLabel = (promoType) => {
  switch (promoType) {
    case PROMO_TYPES.PERCENT:
      return "אחוז הנחה";
    case PROMO_TYPES.QUANTITY_PRICE:
      return "X ב-Y";
    case PROMO_TYPES.PACKAGE_PRICE:
      return "מחיר מארז";
    case PROMO_TYPES.FLAT:
    default:
      return "מחיר";
  }
};
export const getPromoTypeLabel = (promoType) => {
  switch (promoType) {
    case PROMO_TYPES.PERCENT:
      return "אחוז הנחה";
    case PROMO_TYPES.QUANTITY_PRICE:
      return "X ב-Y";
    case PROMO_TYPES.PACKAGE_PRICE:
      return "מחיר מארז";
    case PROMO_TYPES.FLAT:
      return "מחיר";
    default:
      return promoType || "";
  }
};
export const getPromoMinQuantityNote = (promo) => {
  if (!promo || isQuantityBasedPromoType(promo.promoType)) return null;
  const minQty = Number(promo.minQuantity);
  if (!Number.isFinite(minQty) || minQty <= 1) return null;
  return `בקניית ${minQty} ומעלה`;
};

const formatBadgeAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? "");
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
};

/**
 * Single display formatter for promotion badges (storefront cards, admin cards,
 * sales management, price compare). Display-only — does not change promo pricing.
 * Pass listPrice in price-compare so package tags show the package unit sale
 * (hero keeps the regular unit price).
 * @returns {{ text: string, icon: 'tag'|'zap'|'package'|'percent'|'sparkles' } | null}
 */
export const formatPromotionBadge = (promo, { listPrice } = {}) => {
  if (!promo) return null;

  const qty = Number(promo.minQuantity);
  const price = Number(promo.discountValue);

  switch (promo.promoType) {
    case PROMO_TYPES.QUANTITY_PRICE: {
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price)) {
        return null;
      }
      return {
        text: `${qty} יח' ב-${formatBadgeAmount(price)} ₪`,
        icon: "tag",
      };
    }
    case PROMO_TYPES.PACKAGE_PRICE: {
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price)) {
        return null;
      }
      // Price-compare passes listPrice: show only the package unit sale in the tag;
      // the hero already shows the regular unit price.
      if (listPrice != null && Number.isFinite(Number(listPrice))) {
        const unitSale = roundMoney(price / qty);
        return {
          text: `${formatBadgeAmount(unitSale)} ₪ ליח׳ בקניית מארז של ${qty} יח׳`,
          icon: "package",
        };
      }
      return {
        text: "מארז משתלם",
        icon: "package",
      };
    }
    case PROMO_TYPES.PERCENT: {
      if (!Number.isFinite(price)) return null;
      const note = Number.isFinite(qty) && qty > 1 ? ` בקניית ${qty}+` : "";
      return {
        text: `${formatBadgeAmount(price)}% הנחה${note}`,
        icon: "percent",
      };
    }
    case PROMO_TYPES.FLAT: {
      if (Number.isFinite(qty) && qty > 1 && Number.isFinite(price)) {
        return {
          text: `${formatBadgeAmount(price)} ₪ ליח׳ בקניית ${qty}+`,
          icon: "zap",
        };
      }
      return {
        text: "מחיר מבצע",
        icon: "sparkles",
      };
    }
    default:
      return null;
  }
};

export const getPromoAdminLabel = (promo) =>
  formatPromotionBadge(promo)?.text ?? null;

export const getPromoBadgeLabel = (promo) =>
  formatPromotionBadge(promo)?.text ?? null;

/** Manager card label for package promos. */
export const getPackageAdminBadgeLabel = (_promo) => "מחיר מארז";

/**
 * Caption ribbon next to card price — only for promos that need wording
 * beyond strikethrough + sale price (quantity/pack deals, or a buy threshold).
 */
export const promoShowsCaptionBadge = (promo) => {
  if (!promo) return false;
  if (isQuantityBasedPromoType(promo.promoType)) return true;
  if (![PROMO_TYPES.FLAT, PROMO_TYPES.PERCENT].includes(promo.promoType)) {
    return false;
  }
  const qty = Number(promo.minQuantity);
  return Number.isFinite(qty) && qty > 1;
};

export const PROMO_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "פעיל" },
  { value: "DISABLED", label: "מוקפא" },
  { value: "DRAFT", label: "טיוטה" },
];

export const DATE_DRIVEN_PROMO_STATUS_OPTIONS = [
  { value: "SCHEDULED", label: "מתוזמן" },
  { value: "EXPIRED", label: "פג תוקף" },
];

export const getPromoStatusLabel = (status) => {
  const option = [
    ...PROMO_STATUS_OPTIONS,
    ...DATE_DRIVEN_PROMO_STATUS_OPTIONS,
  ].find((item) => item.value === status);
  return option?.label || status || "";
};

export const isDateDrivenPromoStatus = (status) =>
  DATE_DRIVEN_PROMO_STATUS_OPTIONS.some((option) => option.value === status);

export const getPromoStatusSelectOptions = (currentStatus) => {
  const options = [...PROMO_STATUS_OPTIONS];
  const dateDriven = DATE_DRIVEN_PROMO_STATUS_OPTIONS.find(
    (option) => option.value === currentStatus,
  );
  if (dateDriven) {
    options.unshift({ ...dateDriven, disabled: true });
  }
  return options;
};
export const getGtinsFromPromotionEntries = (entries) => {
  const gtins = new Set();
  for (const entry of entries || []) {
    if (entry?.gtin) gtins.add(entry.gtin);
    for (const tp of entry?.targetProducts || []) {
      const gtin = typeof tp === "string" ? tp : tp?.gtin;
      if (gtin) gtins.add(gtin);
    }
  }
  return [...gtins];
};
/** Merge API rows that share the same promoId into one promotion with all barcodes. */
export const groupPromotionsByPromoId = (promotions) => {
  const grouped = new Map();
  for (const promo of promotions || []) {
    const id = getPromoId(promo);
    if (!id) continue;
    const idStr = String(id);
    if (!grouped.has(idStr)) {
      grouped.set(idStr, {
        ...promo,
        targetProducts: getGtinsFromPromotionEntries([promo]),
      });
      continue;
    }
    const existing = grouped.get(idStr);
    existing.targetProducts = [
      ...new Set([
        ...existing.targetProducts,
        ...getGtinsFromPromotionEntries([promo]),
      ]),
    ];
  }
  return Array.from(grouped.values());
};

/** Offset for Asia/Jerusalem on the given instant, e.g. "+03:00". */
const getJerusalemOffset = (date = new Date()) => {
  for (const timeZoneName of ["longOffset", "shortOffset"]) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Jerusalem",
      timeZoneName,
    }).formatToParts(date);
    const name =
      parts.find((part) => part.type === "timeZoneName")?.value || "";
    const match = name.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/i);
    if (match) {
      const sign = match[1];
      const hours = String(match[2]).padStart(2, "0");
      const minutes = String(match[3] || "00").padStart(2, "0");
      return `${sign}${hours}:${minutes}`;
    }
  }
  return "+02:00";
};

export const formatDateTimeForJerusalem = (
  dateString,
  { endOfDay = false } = {},
) => {
  if (!dateString) return "";
  const dateOnly = String(dateString).split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const time = endOfDay ? "23:59:59.999" : "00:00:00.000";
    const offset = getJerusalemOffset(new Date(`${dateOnly}T12:00:00Z`));
    return `${dateOnly}T${time}${offset}`;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const jerusalemTime = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const parts = {};
  jerusalemTime.forEach((part) => {
    parts[part.type] = part.value;
  });
  const time = endOfDay
    ? "23:59:59.999"
    : `${parts.hour}:${parts.minute}:${parts.second}.000`;
  const offset = getJerusalemOffset(date);
  return `${parts.year}-${parts.month}-${parts.day}T${time}${offset}`;
};
export const toDateInputValue = (dateTime) => {
  if (!dateTime) return "";
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const readErrorField = (value) => {
  if (value == null) return null;
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") {
        return (
          readErrorField(parsed.error) ||
          readErrorField(parsed.message) ||
          readErrorField(parsed.Message)
        );
      }
    } catch {
      /* plain string */
    }
    if (/^Request failed with status code \d+/i.test(trimmed)) return null;
    return trimmed;
  }
  if (typeof value === "object") {
    return (
      readErrorField(value.error) ||
      readErrorField(value.message) ||
      readErrorField(value.Message) ||
      readErrorField(value.body)
    );
  }
  return null;
};

const extractApiErrorText = (error) =>
  readErrorField(error?.response?.data) ||
  readErrorField(error?.apiErrorMessage) ||
  readErrorField(error?.message);

const STATIC_PROMOTION_ERROR_MESSAGES = {
  "Missing required fields": "חסרים שדות חובה",
  "Missing request body": "חסר גוף בקשה",
  "Invalid JSON format": "פורמט בקשה לא תקין",
  "Branch 4 is deprecated": "סניף 4 אינו נתמך",
  "Invalid promoType.": "סוג מבצע לא תקין",
  "Invalid status.": "סטטוס לא תקין",
  "endDate must be greater than startDate":
    "תאריך הסיום חייב להיות אחרי תאריך ההתחלה",
  "endDate must be after startDate": "תאריך הסיום חייב להיות אחרי תאריך ההתחלה",
  "Promotion not found": "המבצע לא נמצא",
  "No fields provided for update": "לא סופקו שדות לעדכון",
  "No valid fields provided for update": "לא סופקו שדות תקינים לעדכון",
  "targetProducts must be provided when updating discountValue":
    "יש לבחור מוצרים בעת עדכון מחיר המבצע",
  "Internal Server Error": "שגיאת שרת פנימית",
  "Failed to update promotion": "עדכון המבצע נכשל",
  "Missing promoId in path": "מזהה מבצע חסר",
  "Missing 'x-tenant' in request headers.": "חסר מזהה ארגון בבקשה",
  "startDate must be in format yyyy-MM-ddTHH:mm:ss.SSS±HH:mm":
    "פורמט תאריך התחלה לא תקין",
  "endDate must be in format yyyy-MM-ddTHH:mm:ss.SSS±HH:mm":
    "פורמט תאריך סיום לא תקין",
};

const translatePromotionApiError = (message) => {
  if (STATIC_PROMOTION_ERROR_MESSAGES[message]) {
    return STATIC_PROMOTION_ERROR_MESSAGES[message];
  }

  let match = message.match(
    /^Promotion effective price \((.+)\) must be lower than current price \((.+)\) for product (.+)$/,
  );
  if (match) {
    return `מחיר המבצע (${match[1]}) חייב להיות נמוך מהמחיר הנוכחי (${match[2]}) עבור מוצר ${match[3]}`;
  }

  match = message.match(
    /^GTIN (.+) already has an ACTIVE promotion for this branch$/,
  );
  if (match) {
    return `למוצר ${match[1]} כבר קיים מבצע פעיל בסניף זה`;
  }

  match = message.match(/^No active price found for GTIN (.+)$/);
  if (match) {
    return `לא נמצא מחיר פעיל למוצר ${match[1]}`;
  }

  match = message.match(
    /^GTIN (.+): For PERCENT promotion, discountValue must be between 0 and 100 \(exclusive\)$/,
  );
  if (match) {
    return `עבור מוצר ${match[1]}: באחוז הנחה, הערך חייב להיות בין 0 ל-100 (לא כולל)`;
  }

  match = message.match(
    /^GTIN (.+): minQuantity \(X\) must be greater than 0 for QUANTITY_PRICE promotion$/,
  );
  if (match) {
    return `עבור מוצר ${match[1]}: במבצע X ב-Y, הכמות חייבת להיות גדולה מ-0`;
  }

  return message;
};

/** Resolve a user-facing promotion API error message (Hebrew when known). */
export const getPromotionApiErrorMessage = (error, fallback) => {
  const apiMessage = extractApiErrorText(error);
  if (!apiMessage) return fallback;
  return translatePromotionApiError(apiMessage);
};

const ALLOWED_PROMO_TYPES = [
  "FLAT",
  "PERCENT",
  "QUANTITY_PRICE",
  "PACKAGE_PRICE",
];
const ALLOWED_PROMO_STATUSES = [
  "ACTIVE",
  "SCHEDULED",
  "EXPIRED",
  "DISABLED",
  "DRAFT",
];

export const getProductCurrentPrice = (product, fallbackPricing = null) => {
  const raw =
    product?.pricing?.price ??
    fallbackPricing?.Product?.price ??
    fallbackPricing?.price ??
    null;
  const price = Number(raw);
  return Number.isFinite(price) ? price : null;
};

/** Mirrors backend calculateEffectivePromoUnitPrice; returns price or Hebrew error. */
export const calculateEffectivePromoUnitPrice = (
  promoType,
  discountValue,
  minQuantity,
  currentPrice,
) => {
  const value = Number(discountValue);
  if (!Number.isFinite(value)) {
    return { error: "יש להזין ערך מבצע תקין" };
  }

  switch (promoType) {
    case "PERCENT": {
      if (value <= 0 || value >= 100) {
        return {
          error: "באחוז הנחה, הערך חייב להיות בין 0 ל-100 (לא כולל)",
        };
      }
      return { price: currentPrice * (1 - value / 100) };
    }
    case "QUANTITY_PRICE":
    case "PACKAGE_PRICE": {
      const qty = Number(minQuantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        return {
          error:
            promoType === "PACKAGE_PRICE"
              ? "במבצע מחיר מארז, כמות המארז חייבת להיות גדולה מ-0"
              : "במבצע X ב-Y, הכמות חייבת להיות גדולה מ-0",
        };
      }
      return { price: value / qty };
    }
    case "FLAT":
    default:
      return { price: value };
  }
};

const formatPromoPrice = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
};

/**
 * Client-side validation before create/update promotion API calls.
 * Returns Hebrew error message, or null when valid.
 */
export const validatePromotionBeforeSave = ({
  branch,
  promoType,
  status,
  discountValue,
  minQuantity,
  startDate,
  endDate,
  targetGtins = [],
  productPriceByGtin = {},
  existingPromotions = [],
  currentPromoId = null,
} = {}) => {
  if (branch == null || String(branch).trim() === "") {
    return "חסר סניף";
  }
  if (String(branch) === "4") {
    return "סניף 4 אינו נתמך";
  }
  if (!promoType || !ALLOWED_PROMO_TYPES.includes(promoType)) {
    return "סוג מבצע לא תקין";
  }
  if (status && !ALLOWED_PROMO_STATUSES.includes(status)) {
    return "סטטוס לא תקין";
  }
  if (!startDate || !endDate) {
    return "אנא מלא תאריכי התחלה וסיום";
  }
  const startMs = new Date(startDate).getTime();
  const endMs = new Date(endDate).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return "אנא מלא תאריכי התחלה וסיום";
  }
  if (endMs <= startMs) {
    return "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
  }

  const value = Number(discountValue);
  if (!Number.isFinite(value)) {
    if (promoType === "PERCENT") return "אנא הזן אחוז הנחה";
    if (promoType === "QUANTITY_PRICE" || promoType === "PACKAGE_PRICE") {
      return "אנא הזן כמות ומחיר למבצע";
    }
    return "אנא הזן מחיר מבצע";
  }
  if (value < 0) {
    return "ערך המבצע לא יכול להיות שלילי";
  }

  if (promoType === "QUANTITY_PRICE" || promoType === "PACKAGE_PRICE") {
    const qty = Number(minQuantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      return promoType === "PACKAGE_PRICE"
        ? "במבצע מחיר מארז, כמות המארז חייבת להיות גדולה מ-0"
        : "במבצע X ב-Y, הכמות חייבת להיות גדולה מ-0";
    }
  }

  const gtins = (targetGtins || []).map(String).filter(Boolean);
  if (gtins.length === 0) {
    return "יש לבחור לפחות מוצר אחד למבצע";
  }

  const effectiveStatus = status || "ACTIVE";

  for (const gtin of gtins) {
    if (effectiveStatus === "ACTIVE") {
      const hasActive = (existingPromotions || []).some((promo) => {
        if (promo?.status !== "ACTIVE") return false;
        const promoId = getPromoId(promo);
        if (
          currentPromoId != null &&
          promoId != null &&
          String(promoId) === String(currentPromoId)
        ) {
          return false;
        }
        const promoGtins = getGtinsFromPromotionEntries([promo]).map(String);
        return promoGtins.includes(gtin) || String(promo?.gtin) === gtin;
      });
      if (hasActive) {
        return `למוצר ${gtin} כבר קיים מבצע פעיל בסניף זה`;
      }
    }

    const currentPrice = productPriceByGtin[gtin];
    if (currentPrice == null || !Number.isFinite(Number(currentPrice))) {
      return `לא נמצא מחיר פעיל למוצר ${gtin}`;
    }

    const result = calculateEffectivePromoUnitPrice(
      promoType,
      value,
      minQuantity,
      Number(currentPrice),
    );
    if (result.error) {
      return `עבור מוצר ${gtin}: ${result.error}`;
    }

    if (result.price >= Number(currentPrice)) {
      return `מחיר המבצע (${formatPromoPrice(result.price)}) חייב להיות נמוך מהמחיר הנוכחי (${formatPromoPrice(currentPrice)}) עבור מוצר ${gtin}`;
    }
  }

  return null;
};
